import type { KetQuaTrangThai, MucDo } from './types.ts';
import type { LoaiToken, TrangThaiGia } from './types-vet.ts';

export { LOAI_TOKEN, type LoaiToken } from './types-vet.ts';

/**
 * Hợp đồng dữ liệu của hai endpoint số liệu — `GET /api/v1/m/audit/thong-ke`
 * (mục 8.2) và `GET /api/v1/m/audit/vet/thong-ke-ai` (mục 7.11 và 8.2).
 *
 * ── TIỀN LÀ CHUỖI, ĐẾM LÀ SỐ — CÓ CHỦ ĐÍCH ───────────────────────────────
 * `chi_phi_ban` và `tiet_kiem_dem` là `numeric(20,6)` ở `audit.chi_phi_llm`
 * (mục 7.8); audit-service (Java) tuần tự hoá `numeric` thành CHUỖI JSON
 * như native (`module/Hang.java`): hai cột tiền đi qua
 * `number` của JavaScript là mất chữ số ở đúng chỗ không ai kiểm.
 *
 * Mọi cột ĐẾM (`so_*`, `tong_token`, bảy `token_*`, `do_tre_tb_ms`) đã được ép
 * `::bigint` ở SQL nên về dưới dạng SỐ. Khai đúng hai nhóm ấy ở đây là cách
 * duy nhất để `tong_token + 1` không im lặng thành phép nối chuỗi.
 *
 * Chỗ tiêu dùng số tiền: `doTien()` ở `./so-thong-ke.ts` và `soTien()` ở `./so-tien.ts`. `LOAI_TOKEN` một bản duy nhất ở `./types-vet.ts`.
 */

/**
 * Bảy cột `token_*` của một dòng gom.
 *
 * Kiểu suy ra từ `LOAI_TOKEN` chứ không liệt kê tay: bảy loại là một PHÂN HOẠCH
 * (QĐ-14) và chỗ duy nhất biết danh sách ấy phải là một. Thêm loại thứ tám mà
 * quên một chỗ thì biểu đồ im lặng vẽ thiếu một lát — đúng lỗi mà QĐ-14 đánh
 * đổi để chặn.
 */
export type LatToken = { [K in LoaiToken as `token_${K}`]: number };

export type BuocThoiGian = 'hour' | 'day' | 'month';

export interface DongMucDo {
  muc_do: MucDo;
  so: number;
}

export interface DongKetQua {
  ket_qua: KetQuaTrangThai;
  so: number;
}

export interface DongHanhDong {
  hanh_dong: string;
  so: number;
}

/** Phần sử dụng AI gọn của trang Tổng quan — không có bóc tách bảy loại. */
export interface SuDungAiGon {
  so_luot: number;
  tong_token: number;
  chi_phi_ban: string;
  /** Chỉ API nền tảng trả, và chỉ cho người có `AI_PRICING.READ`: giá vốn, cùng đơn vị với `chi_phi_ban`. */
  chi_phi_von?: string;
  don_vi_tien: string | null;
  so_chua_co_gia: number;
  /** Phần của `so_chua_co_gia` do thiếu tỷ giá. Máy chủ cũ không trả. */
  so_thieu_ty_gia?: number;
  so_uoc_tinh: number;
  so_co_canh_bao: number;
}

/**
 * Phép ĐẾM thật của kỳ, không suy từ danh mục nào.
 *
 * `so_nguoi_hoat_dong` là `count(DISTINCT actor_id)` ở audit-service, không
 * phải độ dài danh mục `nguoi` của `/bo-loc` — danh mục ấy bị cắt ở 500 và một
 * cận dưới hiện lên như một số đo là cách báo cáo tháng ghi "500 người dùng"
 * cho một tenant có 900. Nó cũng KHÔNG đếm các lượt chưa xác định được người
 * (đăng nhập thất bại): gộp chúng lại là đếm một kẻ đang dò mật khẩu như một
 * nhân viên đang làm việc.
 */
export interface TongHopKy {
  so_thao_tac: number;
  so_nguoi_hoat_dong: number;
  so_don_vi: number;
  so_trace: number;
}

export interface MocThaoTac {
  /** Mốc đã `date_trunc` theo `buoc_thoi_gian`. */
  moc: string;
  so: number;
  so_that_bai: number;
}

export interface ThongKe {
  tong_hop: TongHopKy;
  /** Tuỳ chọn: audit-service bản cũ chưa trả hai trường này, và trang không được trắng vì thế. */
  buoc_thoi_gian?: BuocThoiGian;
  theo_thoi_gian?: MocThaoTac[];
  theo_muc_do: DongMucDo[];
  theo_ket_qua: DongKetQua[];
  top_hanh_dong: DongHanhDong[];
  su_dung_ai: SuDungAiGon;
}

export interface TongAi extends LatToken {
  so_luot: number;
  tong_token: number;
  chi_phi_ban: string;
  /** Chỉ API nền tảng trả, và chỉ cho người có `AI_PRICING.READ`: giá vốn, cùng đơn vị với `chi_phi_ban`. */
  chi_phi_von?: string;
  tiet_kiem_dem: string;
  don_vi_tien: string | null;
  so_chua_co_gia: number;
  /** Phần của `so_chua_co_gia` do thiếu tỷ giá. Máy chủ cũ không trả. */
  so_thieu_ty_gia?: number;
  so_uoc_tinh: number;
  so_co_canh_bao: number;
  so_trace: number;
  so_mo_hinh: number;
}

export interface MocAi extends LatToken {
  /** Mốc đã `date_trunc` theo `buoc_thoi_gian`, ISO 8601 có lệch giờ. */
  moc: string;
  so_luot: number;
  tong_token: number;
  chi_phi_ban: string;
  tiet_kiem_dem: string;
  don_vi_tien: string | null;
}

export interface NhomAi {
  /** `(không khai)` khi cột gom là NULL — audit-service đặt tên ở SQL. */
  khoa: string;
  so_luot: number;
  tong_token: number;
  chi_phi_ban: string;
  /** Chỉ API nền tảng trả, và chỉ cho người có `AI_PRICING.READ`: giá vốn, cùng đơn vị với `chi_phi_ban`. */
  chi_phi_von?: string;
  don_vi_tien: string | null;
  so_chua_co_gia: number;
  /** Phần của `so_chua_co_gia` do thiếu tỷ giá. Máy chủ cũ không trả. */
  so_thieu_ty_gia?: number;
  so_co_canh_bao: number;
  /** `null` = KHÔNG lượt nào khai độ trễ. Khác hẳn `0`; giao diện hiện `—`. */
  do_tre_tb_ms: number | null;
  so_that_bai: number;
}

export interface ThongKeAi {
  buoc_thoi_gian: BuocThoiGian;
  tong: TongAi;
  theo_thoi_gian: MocAi[];
  theo_mo_hinh: NhomAi[];
  theo_service: NhomAi[];
  theo_tinh_nang: NhomAi[];
  theo_don_vi: NhomAi[];
  theo_nguoi_dung: NhomAi[];
  /** Chỉ API nền tảng trả (`khoa` là `tenant_id`). */
  theo_tenant?: NhomAi[];
}

/**
 * Năm phép phân rã của mục 8.2, theo thứ tự hiện trên thanh chọn; cổng nền tảng thêm `theo_tenant` ở đầu
 * (`NHOM_PHAN_RA_NEN_TANG`).
 */
export const NHOM_PHAN_RA = [
  'theo_mo_hinh',
  'theo_service',
  'theo_tinh_nang',
  'theo_don_vi',
  'theo_nguoi_dung',
] as const;

export const NHOM_PHAN_RA_NEN_TANG = ['theo_tenant', ...NHOM_PHAN_RA] as const;

export type NhomPhanRa = (typeof NHOM_PHAN_RA_NEN_TANG)[number];

/**
 * `GET /api/v1/m/audit/canh-bao/tom-tat` — ô "cảnh báo chưa xử lý" (mục 8.2).
 *
 * Toàn bộ phản hồi ĐÃ chỉ tính cảnh báo chưa xử lý (`trang_thai = ANY('moi',
 * 'dang_xu_ly')`), nên `tong` là chính con số của ô — không phải tổng mọi cảnh
 * báo từng phát sinh. `theo_muc` luôn có đủ bốn mức kể cả khi bằng 0, vì `{}` sẽ
 * làm giao diện phải tự đoán có phải mình lọc sai hay không.
 */
export interface TomTatCanhBao {
  tong: number;
  theo_muc: Record<string, number>;
  theo_trang_thai: Record<string, number>;
}

/** Hai mức đáng gọi người xử lý ngay — mục 8.6. */
export const MUC_CANH_BAO_NANG = ['cao', 'nghiem_trong'] as const;

/** Cả hai mốc tuỳ chọn — endpoint này có mặc định, khác `GET /bo-loc`. */
export interface TruyVanThongKe {
  tu?: string;
  den?: string;
}

/** Cùng tám ô lọc của `/vet/luot-goi` — `/thong-ke-ai` dùng chung `ThamSoLuot` ở audit-service. */
export interface TruyVanThongKeAi {
  tu: string;
  den: string;
  buoc_thoi_gian: BuocThoiGian;
  mo_hinh?: string;
  nha_cung_cap?: string;
  service?: string;
  tinh_nang?: string;
  nguoi_dung_id?: string;
  don_vi_id?: string;
  trang_thai?: TrangThaiGia;
  co_canh_bao?: boolean;
}
